(() => {
  'use strict';

  if (window.__wiProductCardLoaded) return;
  window.__wiProductCardLoaded = true;

  const VARIANT_STATE_SECTION = 'wi-variant-state';
  const variantStateCache = new Map();
  const variantStatePending = new Map();

  const getI18n = () => {
    const node = document.querySelector('[data-wi-i18n]');
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('[WI Product Card] Unable to parse translations.', error);
      return {};
    }
  };

  const formatTemplate = (template = '', values = {}) =>
    Object.entries(values).reduce(
      (output, [key, value]) => output.split(`__${key}__`).join(String(value)),
      template
    );

  const cardI18n = getI18n().productCard || {};

  const cartRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const parseHtml = (html) => new DOMParser().parseFromString(html, 'text/html');

  const renderServerMarkup = (node, html = '') => {
    if (!node) return;

    if (!html) {
      node.replaceChildren();
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    node.replaceChildren(template.content.cloneNode(true));
  };

  const readVariantState = (doc) => {
    const script = doc.querySelector('[data-wi-variant-state-json]');
    if (!script) throw new Error('Variant state is missing from the response.');

    const payload = JSON.parse(script.textContent || '{}');
    return payload && typeof payload === 'object' ? payload : {};
  };

  const rememberVariantState = (key, html) => {
    variantStateCache.set(key, html);

    if (variantStateCache.size > 30) {
      variantStateCache.delete(variantStateCache.keys().next().value);
    }
  };


  const abortError = () => new DOMException('Aborted', 'AbortError');

  const getVariantStateHtml = async (requestKey, signal) => {
    if (signal?.aborted) throw abortError();

    const cached = variantStateCache.get(requestKey);
    if (cached) return cached;

    let pending = variantStatePending.get(requestKey);

    if (!pending) {
      pending = fetch(requestKey, {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Variant update failed (${response.status}).`);
          }

          return response.text();
        })
        .then((html) => {
          rememberVariantState(requestKey, html);
          return html;
        })
        .finally(() => {
          variantStatePending.delete(requestKey);
        });

      variantStatePending.set(requestKey, pending);
    }

    const html = await pending;
    if (signal?.aborted) throw abortError();
    return html;
  };

  if (!customElements.get('wi-product-card')) {
    customElements.define(
      'wi-product-card',
      class WIProductCard extends HTMLElement {
        connectedCallback() {
          if (this.dataset.wiReady === 'true') return;
          this.dataset.wiReady = 'true';

          this.optionButtons = Array.from(this.querySelectorAll('[data-wi-option-button]'));
          this.variantInput = this.querySelector('[data-wi-variant-id]');
          this.form = this.querySelector('[data-wi-product-form]');
          this.submitButton = this.querySelector('[data-wi-submit]');
          this.status = this.querySelector('[data-wi-card-status]');
          this.price = this.querySelector('[data-wi-price]');
          this.compare = this.querySelector('[data-wi-compare]');
          this.discount = this.querySelector('[data-wi-discount]');
          this.unitPrice = this.querySelector('[data-wi-unit-price]');
          this.image = this.querySelector('.wi-product-card__image');
          this.productLinks = Array.from(this.querySelectorAll('[data-wi-product-link]'));
          this.productTitle = this.querySelector('[data-wi-product-title]');
          this.variantRequestController = null;
          this.recommendationParams = new Map();

          try {
            const initialProductUrl = new URL(
              this.dataset.productUrl || window.location.pathname,
              window.location.origin
            );

            initialProductUrl.searchParams.forEach((value, key) => {
              if (key.startsWith('pr_')) {
                this.recommendationParams.set(key, value);
              }
            });
          } catch (_) {
            // Recommendation tracking is optional.
          }

          this.currentVariant =
            this.variantInput?.value && !this.variantInput.disabled
              ? {
                  id: this.variantInput.value,
                  available: true
                }
              : null;

          this.optionButtons.forEach((button) => {
            button.addEventListener('click', () => this.selectOption(button));
            button.addEventListener('pointerenter', () => this.prefetchOption(button), { passive: true });
            button.addEventListener('pointerdown', () => this.prefetchOption(button), { passive: true });
            button.addEventListener('focus', () => this.prefetchOption(button));
          });

          this.form?.addEventListener('submit', (event) => this.addToCart(event));
          this.syncAvailability();
        }

        disconnectedCallback() {
          this.variantRequestController?.abort();
          this.variantRequestController = null;
        }

        getOptionGroups() {
          return Array.from(this.querySelectorAll('[data-option-position]'))
            .filter((group) => group.matches('fieldset'))
            .sort(
              (a, b) =>
                Number(a.dataset.optionPosition || 0) -
                Number(b.dataset.optionPosition || 0)
            );
        }

        getSelectedOptionIds() {
          return this.getOptionGroups()
            .map((group) =>
              group.querySelector('[data-wi-option-button][aria-pressed="true"]')?.dataset
                .optionValueId || ''
            )
            .filter(Boolean);
        }

        hasCompleteSelection() {
          const groups = this.getOptionGroups();
          if (!groups.length) return true;
          return this.getSelectedOptionIds().length === groups.length;
        }

        getSelectionSnapshot() {
          return this.getOptionGroups().map(
            (group) =>
              group.querySelector('[data-wi-option-button][aria-pressed="true"]')?.dataset
                .optionValueId || ''
          );
        }

        restoreSelection(snapshot = []) {
          this.getOptionGroups().forEach((group, index) => {
            const selectedId = snapshot[index] || '';

            group.querySelectorAll('[data-wi-option-button]').forEach((button) => {
              const active = button.dataset.optionValueId === selectedId;
              button.classList.toggle('is-selected', active);
              button.setAttribute('aria-pressed', active ? 'true' : 'false');
            });
          });
        }

        markSelected(button) {
          const group = button.closest('fieldset[data-option-position]');
          if (!group) return;

          group.querySelectorAll('[data-wi-option-button]').forEach((option) => {
            const active = option === button;
            option.classList.toggle('is-selected', active);
            option.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        }

        syncAvailability() {
          this.optionButtons.forEach((button) => {
            const available = button.dataset.optionAvailable !== 'false';
            button.disabled = !available;
            button.classList.toggle('is-unavailable', !available);
            button.setAttribute('aria-disabled', available ? 'false' : 'true');
          });
        }

        applyOptionStates(doc) {
          const states = Array.from(doc.querySelectorAll('[data-wi-option-state]'));
          const byId = new Map();
          const byValue = new Map();

          states.forEach((state) => {
            if (state.dataset.optionValueId) {
              byId.set(state.dataset.optionValueId, state);
            }

            byValue.set(
              `${state.dataset.optionPosition || ''}::${state.dataset.optionValue || ''}`,
              state
            );
          });

          this.optionButtons.forEach((button) => {
            const fallbackKey = `${button.dataset.optionPosition || ''}::${button.dataset.optionValue || ''}`;
            const state = byId.get(button.dataset.optionValueId) || byValue.get(fallbackKey);
            if (!state) return;

            button.dataset.optionValueId = state.dataset.optionValueId || button.dataset.optionValueId;
            button.dataset.productUrl = state.dataset.productUrl || button.dataset.productUrl;
            button.dataset.optionAvailable = state.dataset.available || 'false';

            // Selection state belongs to the customer, not Shopify's fallback selection.
            // Keep the locally pressed option and only refresh availability metadata here.
          });

          this.syncAvailability();
        }

        setVariantLoading(loading) {
          this.classList.toggle('is-variant-loading', loading);

          if (this.submitButton) {
            if (loading) {
              this.submitButton.disabled = true;
              this.submitButton.setAttribute('aria-busy', 'true');
            } else {
              this.submitButton.removeAttribute('aria-busy');
              this.submitButton.disabled = !this.currentVariant?.available;
            }
          }
        }

        getProspectiveOptionIds(button) {
          const targetGroup = button.closest('fieldset[data-option-position]');

          return this.getOptionGroups()
            .map((group) => {
              if (group === targetGroup) {
                return button.dataset.optionValueId || '';
              }

              return (
                group.querySelector('[data-wi-option-button][aria-pressed="true"]')?.dataset
                  .optionValueId || ''
              );
            })
            .filter(Boolean);
        }

        getVariantRequest(button, selectedIds) {
          const requestedProductUrl =
            button.dataset.productUrl || this.dataset.productUrl || window.location.pathname;
          const url = new URL(requestedProductUrl, window.location.origin);
          url.searchParams.set('section_id', VARIANT_STATE_SECTION);
          url.searchParams.set('option_values', selectedIds.join(','));

          return {
            requestedProductUrl,
            requestKey: url.toString()
          };
        }

        preloadVariantImage(html) {
          if (!this.image || !html) return;

          try {
            const doc = parseHtml(html);
            const payload = readVariantState(doc);
            const variantImage = payload?.variant?.image;

            if (!variantImage?.src) return;

            const preloadImage = new Image();
            preloadImage.loading = 'eager';
            preloadImage.decoding = 'async';
            preloadImage.fetchPriority = 'high';

            if (this.image.sizes) preloadImage.sizes = this.image.sizes;
            if (variantImage.srcset) preloadImage.srcset = variantImage.srcset;

            preloadImage.src = variantImage.src;
          } catch (_) {
            // Prefetching is an optimization only. Normal selection still handles errors.
          }
        }

        prefetchOption(button) {
          if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
          if (button.getAttribute('aria-pressed') === 'true') return;

          const selectedIds = this.getProspectiveOptionIds(button);
          if (!selectedIds.length) return;

          const { requestKey } = this.getVariantRequest(button, selectedIds);

          getVariantStateHtml(requestKey)
            .then((html) => {
              if (selectedIds.length === this.getOptionGroups().length) {
                this.preloadVariantImage(html);
              }
            })
            .catch(() => {});
        }

        async selectOption(button) {
          if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
          if (button.getAttribute('aria-pressed') === 'true') return;

          const previousSelection = this.getSelectionSnapshot();
          this.markSelected(button);

          const selectedIds = this.getSelectedOptionIds();
          if (!selectedIds.length) {
            this.restoreSelection(previousSelection);
            return;
          }

          const { requestedProductUrl, requestKey } = this.getVariantRequest(
            button,
            selectedIds
          );
          const controller = new AbortController();

          this.variantRequestController?.abort();
          this.variantRequestController = controller;
          this.setVariantLoading(true);
          if (this.status) this.status.textContent = '';

          try {
            const html = await getVariantStateHtml(requestKey, controller.signal);

            const doc = parseHtml(html);
            const payload = readVariantState(doc);

            this.applyOptionStates(doc);
            this.dataset.productUrl = payload.productUrl || requestedProductUrl;

            // Partial option selections are used only to refresh availability.
            // Do not resolve a fallback variant, change the image, or enable Add to Cart
            // until the customer has explicitly selected one value from every option group.
            if (this.hasCompleteSelection()) {
              this.updateCard(payload.variant, payload);
            }
          } catch (error) {
            if (error?.name === 'AbortError') return;

            console.warn('[WI Product Card] Variant update failed.', error);
            this.restoreSelection(previousSelection);
            this.syncAvailability();

            if (this.status) {
              this.status.textContent = cardI18n.updateOptionError;
            }
          } finally {
            if (this.variantRequestController === controller) {
              this.variantRequestController = null;
              this.setVariantLoading(false);
            }
          }
        }

        withRecommendationTracking(urlValue) {
          if (!urlValue || !this.recommendationParams?.size) return urlValue;

          try {
            const url = new URL(urlValue, window.location.origin);

            this.recommendationParams.forEach((value, key) => {
              if (!url.searchParams.has(key)) {
                url.searchParams.set(key, value);
              }
            });

            return `${url.pathname}${url.search}${url.hash}`;
          } catch (_) {
            return urlValue;
          }
        }

        updateCard(variant, payload = {}) {
          this.currentVariant = variant || null;

          if (!variant) {
            if (this.variantInput) {
              this.variantInput.value = '';
              this.variantInput.disabled = true;
            }

            if (this.submitButton) {
              const label = this.submitButton.querySelector('[data-wi-cta-label]');
              this.submitButton.disabled = true;
              this.submitButton.setAttribute('aria-disabled', 'true');
              if (label) label.textContent = cardI18n.unavailable;
            }

            if (this.status) this.status.textContent = cardI18n.optionUnavailable;
            return;
          }

          if (this.variantInput) {
            this.variantInput.value = variant.id;
            this.variantInput.disabled = !variant.available;
            this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          renderServerMarkup(this.price, variant.priceText || '');

          const hasDiscount =
            Number(variant.compareAtPrice || 0) > Number(variant.price || 0);

          if (this.compare) {
            renderServerMarkup(this.compare, hasDiscount ? variant.compareText || '' : '');
            this.compare.classList.toggle('is-hidden', !hasDiscount);
          }

          if (this.discount) {
            const percentage = hasDiscount
              ? Math.round(
                  ((Number(variant.compareAtPrice) - Number(variant.price)) /
                    Number(variant.compareAtPrice)) *
                    100
                )
              : 0;

            const discountTemplate =
              this.dataset.discountTemplate ||
              cardI18n.percentOff ||
              '__PERCENT__% OFF';

            this.discount.textContent = hasDiscount
              ? formatTemplate(discountTemplate, { PERCENT: percentage })
              : '';
            this.discount.classList.toggle('is-hidden', !hasDiscount);
          }

          if (this.unitPrice) {
            renderServerMarkup(this.unitPrice, variant.unitPriceText || '');
            this.unitPrice.hidden = !variant.unitPriceText;
          }

          if (this.image && variant.image?.src) {
            this.image.loading = 'eager';
            this.image.fetchPriority = 'high';
            this.image.decoding = 'async';

            if (variant.image.srcset) {
              this.image.srcset = variant.image.srcset;
            } else {
              this.image.removeAttribute('srcset');
            }

            this.image.src = variant.image.src;
            this.image.alt = variant.image.alt || payload.productTitle || this.image.alt;

            this.image.addEventListener(
              'load',
              () => {
                this.image.fetchPriority = 'auto';
              },
              { once: true }
            );
          }

          if (payload.productTitle && this.productTitle) {
            this.productTitle.textContent = payload.productTitle;
          }

          const productLink = this.withRecommendationTracking(
            variant.url || payload.productUrl || this.dataset.productUrl || '#'
          );

          this.productLinks.forEach((link) => {
            link.href = productLink;
          });

          if (this.submitButton) {
            const label = this.submitButton.querySelector('[data-wi-cta-label]');
            const addLabel = this.submitButton.dataset.addLabel;
            const soldOutLabel = this.submitButton.dataset.soldOutLabel || cardI18n.soldOut;

            this.submitButton.disabled = !variant.available;
            this.submitButton.setAttribute(
              'aria-disabled',
              variant.available ? 'false' : 'true'
            );

            if (label) {
              label.textContent = variant.available ? addLabel : soldOutLabel;
            }
          }

          if (this.status) this.status.textContent = '';
        }

        async addToCart(event) {
          event.preventDefault();

          if (!this.form || !this.submitButton || this.submitButton.disabled) return;
          if (this.submitButton.classList.contains('is-loading')) return;

          const originalLabel =
            this.submitButton.querySelector('[data-wi-cta-label]')?.textContent ||
            this.submitButton.dataset.addLabel;

          const label = this.submitButton.querySelector('[data-wi-cta-label]');

          this.submitButton.classList.add('is-loading');
          this.submitButton.setAttribute('aria-busy', 'true');
          this.submitButton.disabled = true;
          if (label) label.textContent = cardI18n.adding;
          if (this.status) this.status.textContent = '';

          try {
            const formData = new FormData(this.form);
            formData.set('sections', 'cart-drawer');
            formData.set('sections_url', `${window.location.pathname}${window.location.search}`);

            const response = await fetch(`${cartRoot()}cart/add.js`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              credentials: 'same-origin',
              body: formData
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.status) {
              throw new Error(
                result.description || result.message || cardI18n.addError
              );
            }

            this.submitButton.classList.remove('is-loading');
            this.submitButton.classList.add('is-added');
            this.submitButton.removeAttribute('aria-busy');

            if (label) label.textContent = cardI18n.added;

            document.dispatchEvent(
              new CustomEvent('wi:cart-item-added', {
                bubbles: true,
                detail: {
                  item: result,
                  sections: result.sections || null,
                  source: 'wi-product-card'
                }
              })
            );

            window.setTimeout(() => {
              if (!this.isConnected) return;

              this.submitButton.classList.remove('is-added');
              this.submitButton.disabled = !this.currentVariant?.available;
              this.submitButton.setAttribute(
                'aria-disabled',
                this.currentVariant?.available ? 'false' : 'true'
              );

              if (label) {
                label.textContent = this.currentVariant?.available
                  ? this.submitButton.dataset.addLabel || originalLabel
                  : this.submitButton.dataset.soldOutLabel || cardI18n.soldOut;
              }
            }, 1300);
          } catch (error) {
            console.warn('[WI Product Card] Add to cart failed.', error);

            this.submitButton.classList.remove('is-loading');
            this.submitButton.removeAttribute('aria-busy');
            this.submitButton.disabled = !this.currentVariant?.available;

            if (label) label.textContent = originalLabel;
            if (this.status) {
              this.status.textContent =
                error?.message || cardI18n.addError;
            }
          }
        }
      }
    );
  }
})();